import { useMemo } from 'react';

import { useAppSelector } from '@/store/hooks';
import { selectSignatureStatusAndColor } from '@/store/selectors/transmissionReportSelectors';
import { useGetNavigationReportIdsQuery } from '@/store/slices/api/reportsApiSlice';

import { SignatureStatus } from '@/types/transmissionReport';

export const useNavigationReportIds = ({ reportId }: { reportId: number }) => {
  const { signatureStatus } = useAppSelector((state) => state.auth);
  const { filters } = useAppSelector((state) => state.preferences);
  const { search } = useAppSelector((state) => state.transmissionReports);
  const reportMeta = useAppSelector((state) => selectSignatureStatusAndColor(state, reportId));
  const LOCAL_STORAGE_KEY = `reportListSortOrder${reportMeta?.color}`;

  const queryParams = useMemo(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    let sortParam: { column: 'DATE_OF_SERVICE'; order: 'ASC' | 'DESC' } | undefined = undefined;
    if (stored === 'ASC' || stored === 'DESC') {
      sortParam = { column: 'DATE_OF_SERVICE', order: stored };
    }
    const isPendingReview = reportMeta?.signatureStatus === SignatureStatus.PendingReview;
    return {
      signatureStatus,
      clinicIds: filters?.clinics.map((clinic) => clinic.value) ?? [],
      practitionerIds: filters?.practitioners.map((practitioner) => practitioner.value) ?? [],
      signingPractitionerIds:
        filters?.signingPractitioners?.map((signingPractitioner) => signingPractitioner.value) ??
        [],
      deviceTypes: filters?.deviceTypes.map((deviceType) => deviceType.value) ?? [],
      vendors: filters?.vendors.map((vendor) => vendor.value) ?? [],
      reportId,
      color: reportMeta?.color,
      search,
      isPendingReview,
      sort: sortParam
    };
  }, [filters, signatureStatus, reportId, search, reportMeta?.color]);

  const shouldFetch = !!queryParams.color || queryParams.isPendingReview;
  const { data, isLoading, isFetching, refetch, ...rest } = useGetNavigationReportIdsQuery(
    queryParams,
    { skip: !shouldFetch }
  );

  const getNextReportId = async () => {
    const { data: navigationReportIds } = await refetch();
    return navigationReportIds?.nextTransmissionReportId;
  };

  const getPreviousReportId = async () => {
    const { data: navigationReportIds } = await refetch();
    return navigationReportIds?.previousTransmissionReportId;
  };

  return { data, isLoading, isFetching, refetch, getNextReportId, getPreviousReportId, ...rest };
};
